// load-images.service.ts
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class LoadImagesService {
  get linkImage(): string {
    return environment.imageBaseUrl;
  }
  private cache: Map<string, { data: Blob, timestamp: number, safeUrl: SafeUrl }> = new Map();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos en milisegundos

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) { }

  /**
   * Obtiene una imagen, ya sea desde la caché o desde la fuente original
   * @param url URL de la imagen a cargar
   * @returns Observable con los datos de la imagen
   */
  getImage(url: string): Observable<Blob> {
    const cachedData = this.cache.get(url);
    const now = Date.now();

    // Si hay datos en caché y no han expirado, devolver desde la caché
    if (cachedData && (now - cachedData.timestamp < this.CACHE_DURATION)) {
      return of(cachedData.data);
    }

    // Si no hay datos en caché o han expirado, hacer la petición
    return this.http.get(url, { responseType: 'blob' }).pipe(
      tap(data => {
        // Guardar en caché con timestamp actual
        this.cache.set(url, {
          data,
          timestamp: now,
          safeUrl: this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(data))
        });
      }),
      catchError(error => {
        console.error('Error al cargar la imagen:', error);
        throw error;
      })
    );
  }

  /**
   * Obtiene una URL segura para una imagen, ya sea desde la caché o cargándola
   * @param imagePath Ruta de la imagen (relativa o absoluta)
   * @returns SafeUrl para usar directamente en el atributo src
   */
  getSafeImageUrl(imagePath: string): Observable<SafeUrl> {
    // Si no hay ruta, devolver null
    if (!imagePath) {
      return of(null as unknown as SafeUrl);
    }

    // Determinar si la URL es completa o relativa
    const fullUrl = imagePath.startsWith('http') ?
      imagePath :
      `${this.linkImage}/${imagePath}`;

    // Verificar si está en caché y no ha expirado
    const cachedData = this.cache.get(fullUrl);
    const now = Date.now();

    if (cachedData && (now - cachedData.timestamp < this.CACHE_DURATION)) {
      return of(cachedData.safeUrl);
    }

    // Si no está en caché, cargar y devolver
    return this.getImage(fullUrl).pipe(
      catchError(error => {
        console.error('Error al obtener imagen segura:', error);
        // En caso de error, devolver la imagen por defecto
        return of(this.sanitizer.bypassSecurityTrustUrl('assets/imagen_default.png'));
      }),
      // Mapear al SafeUrl almacenado en caché
      map(() => {
        // La imagen ya está en caché después de getImage
        const cachedImage = this.cache.get(fullUrl);
        if (cachedImage && cachedImage.safeUrl) {
          return cachedImage.safeUrl;
        } else {
          // Si por alguna razón no está en caché, devolver imagen por defecto
          return this.sanitizer.bypassSecurityTrustUrl('assets/imagen_default.png');
        }
      })
    );
  }

  /**
   * Construye la URL completa para una imagen
   * @param path Ruta relativa de la imagen
   * @returns URL completa de la imagen
   */
  getImageUrl(path: string): string {
    if (!path) return '';
    return path.startsWith('http') ? path : `${this.linkImage}/${path}`;
  }

  /**
   * Limpia la caché de imágenes
   */
  clearCache(): void {
    this.cache.clear();
  }
}
